/**
 * Keeps the header cart badge and the cart drawer in step with the real cart.
 * A page restored from the back/forward cache, or a tab left open while the
 * cart was bought or replaced, still shows the cart it was rendered with. This
 * checks /cart.js when the page is shown again, when the tab regains focus and
 * when the drawer opens. When the count differs, it clears the badge at once
 * if the cart is empty, then re-renders the badge and drawer partials.
 */
const root = window.Shopify?.routes?.root || '/';
let inFlight = null;

const trigger = () => document.getElementById('header-cart-trigger');
const renderedCount = () => Number.parseInt(trigger()?.dataset.cartCount ?? '0', 10) || 0;

function clearBadge() {
  const button = trigger();
  if (!button) return;
  button.querySelector('[data-cart-badge]')?.remove();
  button.dataset.cartCount = '0';
}

async function refreshPartials() {
  await customElements.whenDefined('s-cart');
  await customElements.get('s-cart')?.refreshMountedCarts?.();
}

async function sync() {
  if (inFlight) return inFlight;
  inFlight = (async () => {
    let count;
    try {
      const response = await fetch(`${root}cart.js`, {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
        credentials: 'same-origin',
      });
      if (!response.ok) return;
      count = Number((await response.json())?.item_count) || 0;
    } catch {
      return;
    }

    if (count === renderedCount()) return;
    if (count === 0) clearBadge();

    try {
      await refreshPartials();
    } catch {
      if (count === 0) clearBadge();
    }
  })().finally(() => { inFlight = null; });
  return inFlight;
}

window.addEventListener('pageshow', (event) => {
  if (event.persisted) sync();
});

document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') sync();
});

const drawer = document.getElementById('cart-drawer');
drawer?.addEventListener('command', sync);
drawer?.addEventListener('toggle', (event) => {
  if (event.newState === 'open') sync();
});
drawer?.addEventListener('s-cart:opened', sync);
